import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // guard against React StrictMode double-invoke
    started.current = true;

    if (!token) {
      setStatus('error');
      setMessage('This verification link is missing its token.');
      return;
    }
    (async () => {
      try {
        await axios.post(`${API_URL}/api/auth/verify-email`, { token }, { withCredentials: true });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center p-4 sm:p-8 relative">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-aura-purple/20 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10 text-center"
      >
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-serif font-medium text-text-light">Memouraa</span>
        </Link>

        {status === 'verifying' && (
          <>
            <div className="w-14 h-14 rounded-full bg-champagne/10 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-7 h-7 text-champagne animate-spin" />
            </div>
            <h1 className="text-2xl font-serif font-medium text-text-light mb-2">Verifying your email&hellip;</h1>
            <p className="text-text-muted">Just a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-success" />
            </div>
            <h1 className="text-3xl font-serif font-medium text-text-light mb-3">Email verified</h1>
            <p className="text-text-muted mb-8">Thanks for confirming. Your account is all set.</p>
            <Link to="/dashboard" className="btn-champagne inline-flex items-center gap-2 px-6 py-3">Go to dashboard</Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-7 h-7 text-error" />
            </div>
            <h1 className="text-3xl font-serif font-medium text-text-light mb-3">Verification failed</h1>
            <p className="text-text-muted mb-8">{message}</p>
            <Link to="/dashboard" className="text-champagne font-medium hover:underline">Go to dashboard</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
